import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import format from "string-template";
import { downloadUploadedDocument } from "utils/helpers/files";
import ActionButtons from "../Components/ActionButtons";
import Header from "../Components/Header";
import Layout from "../Components/Layout";
import DocumentUpload from "../Justification/DocumentUpload";
import IdentityDocumentSides from "../Justification/IdentityDocumentSides";
import { getAttestationHint } from "../utils/attestationHint";
import validation from "./validation";

const CIN_AGE_THRESHOLD = 18;

function View({
  initialValues,
  onSubmit,
  downloadFile,
  onBack,
  translate,
  onFileRemove: handleFileRemove,
  supportingDocuments = [],
  subscription,
  project,
}) {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const { isValid, submitForm, setFieldValue, values, ...formProps } =
    useFormik({
      initialValues,
      enableReinitialize: true,
      onSubmit: onSubmit,
      validationSchema: validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
        get(initialValues, "includedChildren", []),
      ),
    });

  const includedChildren = get(values, "includedChildren", []);

  if (!includedChildren || !Array.isArray(includedChildren)) {
    return <>{translate?.LOADING || "Chargement …"}</>;
  }

  const createFileRemoveHandler = (fieldName) => async (item) => {
    if (handleFileRemove) {
      await handleFileRemove(fieldName)(item);
      const currentFiles = get(values, fieldName, []) || [];
      const updatedFiles = currentFiles.filter(
        (file) => (file?.uid || file?.id) !== (item?.uid || item?.id),
      );
      setFieldValue(fieldName, updatedFiles);
    }
  };

  const handleDownload = ({ uid, size, base64, contentType, name }) => {
    if (!size) return downloadFile(uid);
    downloadUploadedDocument({
      base64,
      contentType,
      name,
    });
  };

  return (
    <Layout>
      <Header
        title={translate?.HEADER?.TITLE || "Pièces justificatives des enfants"}
        highlight={translate?.HEADER?.HIGHLIGHT || "pièces justificatives"}
        subtitle={
          translate?.HEADER?.SUBTITLE ||
          "Pièces justificatives des enfants à charge"
        }
      />

      <div className="acoris-form">
        <div className="acoris-form__container">
          <div className="acoris__children-justification">
            {includedChildren.map((child, index) => {
              let title;
              if (index === 0) {
                title = translate?.CHILD_TITLES?.FIRST || "Premier enfant";
              } else if (index === 1) {
                title = translate?.CHILD_TITLES?.SECOND || "Deuxième enfant";
              } else if (index === 2) {
                title = translate?.CHILD_TITLES?.THIRD || "Troisième enfant";
              } else {
                title = translate?.CHILD_TITLES?.NTH
                  ? format(translate.CHILD_TITLES.NTH, { n: index + 1 })
                  : `${index + 1}ème enfant`;
              }

              // Get child's name
              const firstName = get(child, "identity.user.firstname", "");
              const lastName = get(child, "identity.user.lastname", "");
              const childName = [firstName, lastName].filter(Boolean).join(" ");
              const titleWithName = childName
                ? `${title} - ${childName}`
                : title;

              const socialSecurityCertificateKey = `socialSecurityCertificate_${child.userId}`;
              const cinRectoKey = `cinRecto_${child.userId}`;
              const cinVersoKey = `cinVerso_${child.userId}`;
              const schoolCertificateKey = `schoolCertificate_${child.userId}`;
              const isAdultChild = get(child, "age", 0) > CIN_AGE_THRESHOLD;
              const attestationHint = getAttestationHint(
                get(child, "socialRegime"),
                {
                  general:
                    translate?.DOCUMENTS?.SOCIAL_SECURITY_CERTIFICATE
                      ?.HINT_GENERAL,
                  local:
                    translate?.DOCUMENTS?.SOCIAL_SECURITY_CERTIFICATE
                      ?.HINT_LOCAL,
                },
              );

              return (
                <div
                  key={child.userId}
                  className="acoris__children-justification__child-section"
                >
                  <h3 className="acoris__children-justification__child-section--title">
                    {titleWithName}
                  </h3>
                  <DocumentUpload
                    label={
                      translate?.DOCUMENTS?.SOCIAL_SECURITY_CERTIFICATE
                        ?.LABEL ||
                      "Attestation de droits de l'Assurance Maladie"
                    }
                    hint={attestationHint}
                    items={get(values, socialSecurityCertificateKey, [])}
                    onDownload={handleDownload}
                    onUpload={(item) => {
                      setFieldValue(socialSecurityCertificateKey, item);
                    }}
                    onFileRemove={createFileRemoveHandler(
                      socialSecurityCertificateKey,
                    )}
                    required
                  />
                  {get(formProps.errors, socialSecurityCertificateKey) && (
                    <span className="acoris__children-justification__error-message">
                      {get(formProps.errors, socialSecurityCertificateKey)}
                    </span>
                  )}
                  {isAdultChild && (
                    <>
                      <IdentityDocumentSides
                        rectoField={cinRectoKey}
                        versoField={cinVersoKey}
                        values={values}
                        setFieldValue={setFieldValue}
                        createFileRemoveHandler={createFileRemoveHandler}
                        handleDownload={handleDownload}
                        errors={formProps.errors}
                        translate={translate}
                      />
                      <DocumentUpload
                        label={
                          translate?.DOCUMENTS?.SCHOOL_CERTIFICATE?.LABEL ||
                          "Certificat de scolarité"
                        }
                        items={get(values, schoolCertificateKey, [])}
                        onDownload={handleDownload}
                        onUpload={(item) => {
                          setFieldValue(schoolCertificateKey, item);
                        }}
                        onFileRemove={createFileRemoveHandler(
                          schoolCertificateKey,
                        )}
                      />
                    </>
                  )}
                </div>
              );
            })}
            <div className="acoris-form__info">
              <p>
                {translate?.FILE_INFO?.SUPPORTED_FORMATS ||
                  "Formats de fichiers pris en charge : JPEG, PNG ou PDF"}
              </p>
              <p>
                {translate?.FILE_INFO?.MAX_SIZE || "Taille maximale : 5 Mo"}
              </p>
            </div>
          </div>

          <ActionButtons
            disabled={!isEmpty(formProps.errors)}
            onNext={submitForm}
            onBack={onBack}
            buttonText={translate?.BUTTON?.NEXT || "Suivant"}
          />
        </div>
      </div>
    </Layout>
  );
}

export default View;
